/**
 * Hoe de configurator een wijziging uit de Vinyl Lettering Designer in zijn
 * configuratie zet. Los van de configurator, zodat getest is dat de configurator
 * daarna precies de configuratie heeft die de host prijst (host: eigen sleutels
 * over `getBaseConfiguration()`). Alleen dan is het dezelfde prijsaanvraag en
 * kan de gedeelde prijsfetch hem één keer doen (Ruling P6).
 *
 * Geeft het oude object terug als er niets verandert: geen nieuwe render en geen
 * nieuwe prijsaanvraag.
 */
import type { VstDesignChange } from './vst-host';
import { hostMessages } from './vst-host-messages';

type Configuratie = Record<string, string | number>;

function gelijk(a: Configuratie, b: Configuratie): boolean {
  const sleutels = Object.keys(b);
  return Object.keys(a).length === sleutels.length && sleutels.every((k) => a[k] === b[k]);
}

export function withDesignChange(
  vorige: Configuratie,
  detail: VstDesignChange,
  product: { hasCopiesOption: boolean; contourOptionId: string | null }
): Configuratie {
  // Maat in CENTIMETER (normalizeCustomSizeOptions rekent x10 voor de leverancier);
  // aantal als string zoals de optiewaarden; contouren alleen met een echte contouroptie.
  const { letter_chars: _chars, letter_lines: _lines, ...zonderTekst } = vorige;
  void _chars;
  void _lines;
  const volgende: Configuratie = {
    ...zonderTekst,
    size: 'custom',
    size_width: detail.widthMm / 10,
    size_height: detail.heightMm / 10,
    ...(product.hasCopiesOption ? { copies: String(detail.copies) } : {}),
    ...(product.contourOptionId && detail.contourCount !== null && detail.contourCount >= 1
      ? { [product.contourOptionId]: String(detail.contourCount) }
      : {}),
    ...(detail.letterText ?? {}),
  };
  return gelijk(vorige, volgende) ? vorige : volgende;
}

export function withDesignMaterial(vorige: Configuratie, optionId: string, value: string): Configuratie {
  return vorige[optionId] === value ? vorige : { ...vorige, [optionId]: value };
}

/** Kant van het glas zoals de designer hem in het ontwerp zet (`design.glassSide`). */
export type VinylGlassSide = 'inside' | 'outside';

/** Optie-id van de leveranciersoptie spiegelen; blijft `normal` als de designer zelf spiegelt (Ruling P8). */
const MIRROR_OPTION_ID = 'mirror';

/**
 * De glaskant van het geregistreerde ontwerp, maar alleen als het bij dit bestand hoort.
 * Een ouder resultaat mag nooit een ander bestand als "gespiegeld" labelen.
 */
export function glassSideFor(
  ontwerp: { uploadId: string; glassSide: VinylGlassSide | null } | null | undefined,
  uploadId: string | null | undefined
): VinylGlassSide | null {
  if (!ontwerp || !uploadId || ontwerp.uploadId !== uploadId) return null;
  return ontwerp.glassSide;
}

/**
 * Ruling P9: bij een ontwerp voor de binnenkant van glas toont de winkelwagen- en orderregel
 * "binnenkant glas – gespiegeld snijbestand". Alleen weergave (`displayOptions`): de
 * leveranciersoptie `mirror` blijft `normal`, want het snijbestand is al gespiegeld en mag
 * niet nog eens gespiegeld worden (Ruling P8). De regel "Spiegelen: Normaal" gaat er bij
 * zo'n ontwerp uit; naast "gespiegeld snijbestand" zou de klant die als tegenstrijdig lezen.
 */
export function withGlassSideDisplay<T extends { key: string; label: string; value: string }>(
  rows: T[],
  glassSide: VinylGlassSide | null | undefined,
  locale: string
): Array<T | { key: string; label: string; value: string }> {
  if (glassSide !== 'inside') return rows;
  const m = hostMessages(locale);
  return [
    ...rows.filter((row) => row.key !== MIRROR_OPTION_ID),
    { key: 'vinylGlassSide', label: m.glassSideLabel, value: m.glassSideInside },
  ];
}
